import React, {useEffect, useState, useRef, useMemo} from 'react'
import parse from 'html-react-parser'
import PropTypes from 'prop-types'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import {FormattedMessage, useIntl} from 'react-intl'
import Helmet from 'react-helmet'

import {
    useMultiStyleConfig,
    Box,
    Flex,
    Select,
    Text,
    FormControl,
    Stack,
    useDisclosure,
    Button,
    Image,
    Modal,
    ModalBody,
    ModalFooter,
    ModalContent,
    ModalCloseButton,
    ModalOverlay,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Skeleton,
    useBreakpointValue,
    VisuallyHidden
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {
    useCategory,
    useCustomerId,
    useProductSearch,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import Link from '@salesforce/retail-react-app/app/components/link'
import AbovePageHeader from '@salesforce/retail-react-app/app/pages/product-list/partials/above-page-header'
import {useSortUrls, useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {HTTPNotFound, HTTPError} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {useSiteCustomPreferences} from '../../../hooks/use-site-custom-preferences'
import {useCarouselObserver} from '../../../hooks/use-carousel-observer.js'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'
import {useGtm} from '../../../integrations/gtm/app/hooks/use-gtm.js'
import {useWishList} from '../../../hooks/use-wish-list.js'
import {useTrackThinkAnalyticsEvents} from '../../../hooks/use-track-thinkanalytics-events.js'
import useSeo from '../../../hooks/use-seo.js'
import {useContentAsset} from '../../../hooks/use-content-asset'

import customStylesPLP from '../../../theme/components/project/plp-custom-styles'
import {einsteinProductRecommendationStyles} from '../../../theme/components/project/einstien-product-recommendation-styles'
import {productCarouselStyles} from '../../../theme/components/project/product-carousel'
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../../components/product-tile'
import Seo from '../../../components/seo/index.jsx'
import EmptySearchResults from './empty-results'
import Breadcrumb from '../../../components/breadcrumb'
import {SubCategoryCarousel} from '../../../components/sub-category-carousel/index.jsx'
import Refinements from './refinements'
import SelectedRefinements from './selected-refinements'
import EinsteinTaSlotHtml from '../../../components/content-slot/einstein-ta-slot-html'
import {FilterIconCR, FilterOrange, SortIcon} from '../../../components/icons'
import {
    getPowerReviewAPIKeys,
    initPowerReviewsForPLPAndPDP
} from '../../../utils/power-reviews-utils'
import CategoryContentAsset from '../../content-detail/category-content-asset'
import AppendTiles from './append-tiles.jsx'
import GlobalContentSlotHtml from '../../../components/content-slot/global-content-slot-html'
import {carouselDataComponentCount} from '../../../utils/panel-selection-utils'
import {categoryUrlBuilder} from '../../../utils/url.js'
import {
    API_ERROR_MESSAGE,
    MAX_CACHE_AGE,
    TOAST_MESSAGE_REMOVED_FROM_WISHLIST,
    STALE_WHILE_REVALIDATE,
    TOAST_MESSAGE_ADDED_TO_GUEST_WISHLIST
} from '../../../constants'

// NOTE: You can ignore certain refinements on a template level by updating the below
// list of ignored refinements.
const REFINEMENT_DISALLOW_LIST = ['c_isNew']
/*
 * This is a simple product listing page. It displays a paginated list
 * of product hit objects. Allowing for sorting and filtering based on the
 * allowable filters and sort refinements.
 */
const CategoryContent = (props) => {
    // Using destructuring to omit properties; we must rename `isLoading` because we use a different
    // `isLoading` later in this function.
    // eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-vars
    const {isLoading: _unusedIsLoading, staticContext, ...rest} = props
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {getcustomPreferencevalue} = useSiteCustomPreferences()
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const history = useHistory()
    const params = useParams()
    const location = useLocation()
    const toast = useToast()
    const activeData = useActiveData()
    const {res} = useServerContext()
    const customerId = useCustomerId()
    const [searchParams, {stringify: stringifySearchParams}] = useSearchParams()
    const configs = getConfig()
    const defaultPageSize = configs?.app?.library?.defaultPageSize
    searchParams.limit = defaultPageSize
    const segment = useSegment()
    const gtm = useGtm()
    const intl = useIntl()
    const einstein = useEinstein()
    const {mutate: trackTAEvent} = useTrackThinkAnalyticsEvents()
    const isTAEnabled = getcustomPreferencevalue('pwaThinkAnalyticsEnabled')
    const isMarketingPersonalizationEnabled = getcustomPreferencevalue(
        'pwaMarketingCloudPersonalizationEnabled'
    )
    const urlParams = new URLSearchParams(location.search)

    /**************** Page State ****************/
    const [filtersLoading, setFiltersLoading] = useState(false)
    const [wishlistLoading, setWishlistLoading] = useState([])
    const [sortOpen, setSortOpen] = useState(false)
    const [showMore, setShowMore] = useState(false)
    const [hasCheckedFilters, setHasCheckedFilters] = useState(false)
    const [isLoaded, setIsLoaded] = useState(false)
    const [clickCount, setClickCount] = useState(0)
    const [tiles, setTiles] = useState([])
    const [offSetValue, setOffSetValue] = useState(0)
    const [selectedFilterCount, setSelectedFilterCount] = useState(0)
    const [disableShowMore, setDisableShowMore] = useState(false)
    const [triggerSearch, setTriggerSearch] = useState(false)
    const [prevTotal, setPrevTotal] = useState(0)

    const loadMore = () => {
        setDisableShowMore(true)
        setClickCount((prevData) => prevData + 1)
        setTriggerSearch(true)
    }

    const filterStyles = useMultiStyleConfig('FilterSection')

    const toggleShowMore = () => setShowMore(!showMore)

    let searchQuery = urlParams.get('q')
    const isSearch = !!searchQuery

    if (params.categoryId) {
        searchParams._refine.push(`cgid=${params.categoryId}`)
    }

    /**************** Mutation Actions ****************/
    const {mutateAsync: createCustomerProductListItem} = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const {mutateAsync: deleteCustomerProductListItem} = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )

    /**************** Query Actions ****************/
    const {
        isLoading,
        isFetched,
        isRefetching,
        data: currentProductSearchResult
    } = useProductSearch(
        {
            parameters: {
                ...searchParams,
                perPricebook: true,
                allVariationProperties: true,
                allImages: true,
                expand: [
                    'promotions',
                    'variations',
                    'prices',
                    'images',
                    'page_meta_tags',
                    'custom_properties'
                ],
                refine: searchParams._refine
            }
        },
        {
            keepPreviousData: true
        }
    )
    const isMobileAppRestrictedSrc = configs?.app?.login?.social?.mobileAppRestricted || 'mobileapp'
    const isMobileAppRestricted =
        typeof window !== 'undefined' &&
        localStorage.getItem('sourceCode') === isMobileAppRestrictedSrc
    const productSearchResult = isMobileAppRestricted
        ? {
              ...currentProductSearchResult,
              hits: currentProductSearchResult?.hits?.filter(
                  (hit) => hit?.productId !== 'sub_product'
              )
          }
        : currentProductSearchResult
    if (isMobileAppRestricted && productSearchResult?.hits < defaultPageSize) {
        productSearchResult.total = productSearchResult?.hits?.length
        productSearchResult.limit = productSearchResult.total
    }
    const {error, data: category} = useCategory(
        {
            parameters: {
                id: params.categoryId
            }
        },
        {
            enabled: !isSearch && !!params.categoryId
        }
    )

    const seoPLPContentAssetId = ['category-title-' + params?.categoryId]
    const seoContentAssets = useContentAsset(seoPLPContentAssetId)
    const seoCategoryTitleContent = useMemo(() => {
        const rawHtml = seoContentAssets?.[seoPLPContentAssetId]?.body || ''
        return parse(rawHtml)
    }, [seoContentAssets, seoPLPContentAssetId])

    // Apply disallow list to refinements.
    if (productSearchResult?.refinements) {
        productSearchResult.refinements = productSearchResult.refinements.filter(
            ({attributeId}) => !REFINEMENT_DISALLOW_LIST.includes(attributeId)
        )
    }

    // SEO data
    const [seoReady, setSeoReady] = useState(false)
    const {seoData} = useSeo({
        pageType: isSearch ? 'page' : 'Collections',
        data: productSearchResult?.pageMetaTags
    })

    useEffect(() => {
        // Wait for data to be fully loaded
        if (productSearchResult?.pageMetaTags) {
            setSeoReady(true)
        }
    }, [productSearchResult])

    const metaTags = productSearchResult?.pageMetaTags?.reduce((obj, metaTag) => {
        obj[metaTag?.id] = metaTag?.value
        return obj
    }, {})

    /**************** Error Handling ****************/
    const errorStatus = error?.response?.status
    switch (errorStatus) {
        case undefined:
            // No Error.
            break
        case 404:
            throw new HTTPNotFound('Category Not Found.')
        default:
            throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
    }

    /**************** Response Handling ****************/
    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    // Reset scroll position when `isRefetching` becomes `true`.
    useEffect(() => {
        if (productSearchResult?.hits?.length > 0 && offSetValue === 0) {
            console.log('offSetValue count :' + productSearchResult.hits.length)
            setOffSetValue(productSearchResult.hits.length)
        }
    }, [productSearchResult])

    useEffect(() => {
        isRefetching
        setFiltersLoading(isRefetching)
    }, [isRefetching])

    /**************** Render Variables ****************/
    // const basePath = `${location?.pathname}${location?.search}`
    const showNoResults =
        !isLoading &&
        productSearchResult &&
        (!productSearchResult?.hits || productSearchResult?.hits?.length === 0)
    const {sortingOptions} = productSearchResult || {}
    const selectedSortingOptionLabel =
        sortingOptions?.find(
            (option) =>
                option.id === (searchParams.sort || productSearchResult?.selectedSortingOption)
        ) ?? sortingOptions?.[0]

    const sortUrls = useSortUrls({options: sortingOptions})

    const showLoadMore =
        !isLoading &&
        productSearchResult?.hits?.length > 0 &&
        productSearchResult?.total >= defaultPageSize

    /**************** Action Handlers ****************/
    const {data: wishlist} = useWishList()
    const addItemToWishlist = async (event, product, productPosition, productDetail) => {
        setWishlistLoading([...wishlistLoading, product.productId])
        const wishListId = wishlist?.productWishList?.find((item) => item?.id)?.id

        await createCustomerProductListItem(
            {
                parameters: {customerId, listId: wishListId},
                body: {
                    quantity: 1,
                    public: false,
                    priority: 1,
                    type: 'product',
                    productId: product?.productId
                }
            },
            {
                onError: () => {
                    toast({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error',
                        isClosable: false,
                        duration: 3000
                    })
                },
                onSuccess: () => {
                    toast({
                        title: formatMessage(TOAST_MESSAGE_ADDED_TO_GUEST_WISHLIST),
                        status: 'success',
                        isClosable: false,
                        duration: 3000
                    })
                    // gtm
                    gtm.sendProductToWishList('addToWishlist', product)
                    // TA
                    if (isTAEnabled) {
                        trackTAEvent({
                            c_productId: product?.productId,
                            c_refUseCase: 'CRSTORE',
                            c_useCase: 'Store%2FLearn%2FClickedOnSearch'
                        })
                    }
                    // segment
                    segment.sendProductClick(productDetail, productPosition)
                    segment.sendProductViewed(event, productDetail)
                    segment.sendProductToWishList(product, wishlist, productPosition, productDetail)
                },
                onSettled: () => {
                    setWishlistLoading(wishlistLoading?.filter((id) => id !== product.productId))
                }
            }
        )
    }

    const removeItemFromWishlist = async (event, product, productPosition, productDetail) => {
        setWishlistLoading([...wishlistLoading, product?.productId])

        const listId = wishlist.productWishList?.find((item) => item?.id)?.id
        const itemId = wishlist?.productWishList
            ?.flatMap((list) => list.customerProductListItems || [])
            .find((item) => item.productId === product?.productId)?.id

        await deleteCustomerProductListItem(
            {
                body: {},
                parameters: {customerId, listId, itemId}
            },
            {
                onError: () => {
                    toast({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error'
                    })
                },
                onSuccess: () => {
                    toast({
                        title: formatMessage(TOAST_MESSAGE_REMOVED_FROM_WISHLIST),
                        status: 'error'
                    })
                    // segment
                    segment.sendProductClick(productDetail, productPosition)
                    segment.sendProductViewed(event, productDetail)
                    segment.sendRemoveProductFromWishlist(
                        product,
                        wishlist?.productWishList,
                        productPosition,
                        productDetail
                    )
                },
                onSettled: () => {
                    setWishlistLoading(wishlistLoading.filter((id) => id !== product.productId))
                }
            }
        )
    }

    // Toggles filter on and off
    const toggleFilter = (value, attributeId, selected, allowMultiple = true) => {
        const searchParamsCopy = {...searchParams}

        // Remove the `offset` search param if present.
        const currentSort = searchParams.sort
        delete searchParamsCopy.offset
        if (currentSort) searchParamsCopy.sort = currentSort

        // If we aren't allowing for multiple selections, simply clear any value set for the
        // attribute, and apply a new one if required.
        if (!allowMultiple) {
            const previousValue = searchParamsCopy.refine[attributeId]
            delete searchParamsCopy.refine[attributeId]

            // Note the loose comparison, for "string != number" checks.
            if (!selected && value.value != previousValue) {
                searchParamsCopy.refine[attributeId] = value.value
            }
        } else {
            // Get the attibute value as an array.
            let attributeValue = searchParamsCopy.refine[attributeId] || []

            // Ensure that the value is still converted into an array if it's a `string` or `number`.
            if (typeof attributeValue === 'string') {
                attributeValue = attributeValue.split('|')
            } else if (typeof attributeValue === 'number') {
                attributeValue = [attributeValue]
            }

            // Either set the value, or filter the value out.
            if (!selected) {
                attributeValue.push(value.value)
            } else {
                // Note the loose comparison, for "string != number" checks.
                attributeValue = attributeValue?.filter((v) => v != value.value)
            }

            // Update the attribute value in the new search params.
            searchParamsCopy.refine[attributeId] = attributeValue

            // If the update value is an empty array, remove the current attribute key.
            if (searchParamsCopy.refine[attributeId].length === 0) {
                delete searchParamsCopy.refine[attributeId]
            }
        }

        if (isSearch) {
            navigate(`/search?${stringifySearchParams(searchParamsCopy)}`)
        } else {
            navigate(`/collections/${params.categoryId}?${stringifySearchParams(searchParamsCopy)}`)
        }
    }

    // Clears all filters
    const resetFilters = () => {
        const newSearchParams = {
            ...searchParams,
            refine: []
        }

        if (searchParams.sort) {
            newSearchParams.sort = searchParams.sort
        }

        const newPath = isSearch
            ? `/search?${stringifySearchParams(newSearchParams)}`
            : `/collections/${params.categoryId}?${stringifySearchParams(newSearchParams)}`

        setSelectedFilterCount(0)
        setHasCheckedFilters(false)
        navigate(newPath)
    }

    /**************** activeData ****************/
    useEffect(() => {
        if (productSearchResult) {
            try {
                if (isSearch) {
                    einstein.sendViewSearch(searchQuery, productSearchResult)
                    activeData.sendViewSearch(searchParams, productSearchResult)
                } else {
                    activeData.sendViewCategory(searchParams, category, productSearchResult)
                    const einsteincategory = {
                        id: productSearchResult.selectedRefinements.cgid
                    }
                    einstein.sendViewCategory(
                        category ? category : einsteincategory,
                        productSearchResult
                    )
                }
            } catch (error) {
                logger.error('Einstein activeData sendViewSearch error', {
                    namespace: 'ProductList.useEffect',
                    additionalProperties: {error: error, searchQuery}
                })
            }
        }
    }, [productSearchResult])

    /* Custom logic to show hide filter */
    const [hideFilter, setIsActive] = useState(false)
    const [isTransitionEnabled, setTransitionEnabled] = useState(false)
    const [transitOnFilter, setTransitOnFilter] = useState(false)

    const showhidefilter = () => {
        setIsActive((current) => !current)
        setTransitionEnabled((current) => !current)
        setTransitOnFilter(true)
    }

    const filterWidth = useBreakpointValue({base: '0', sm: '45%', md: '30%', lg: '23.2%'})
    const isMobile = useBreakpointValue({base: true, sm: false})
    const isTablet = useBreakpointValue({base: false, sm: true})

    useEffect(() => {
        const showReviews = async () => {
            if (productSearchResult?.hits) {
                const result = await getPowerReviewAPIKeys(getcustomPreferencevalue)
                if (!result) return
                initPowerReviewsForPLPAndPDP(result)
            }
        }
        showReviews()
    }, [productSearchResult?.hits, getcustomPreferencevalue])

    /***************** GTM ************/
    useEffect(() => {
        if (gtm && productSearchResult?.hits && productSearchResult.hits.length > 0) {
            gtm.sendSearchImpressionData('search')
        }
    }, [gtm, productSearchResult?.hits])

    /***************** Segment ************/
    useEffect(() => {
        if (productSearchResult?.hits && productSearchResult.hits.length > 0) {
            segment.sendProductListViewed(productSearchResult, category)
        }
    }, [productSearchResult?.hits])

    const handleViewItems = () => {
        // Explicitly close the modal when "View X Items" button is clicked in Mobile
        onClose()
    }
    /***************** Pagination ************/
    // State variable near the top with other state variables
    const [displayedProductCount, setDisplayedProductCount] = useState(0)
    const selectedRefinements = productSearchResult?.selectedRefinements || {}
    const hasSelectedFilters = Object.entries(selectedRefinements).some(
        ([key, val]) => key !== 'htype' && key !== 'cgid' && val
    )

    // To track the total number of displayed products
    useEffect(() => {
        const initialProductCount = Array.isArray(productSearchResult?.hits)
            ? productSearchResult.hits.length
            : 0
        const loadedTilesCount = Array.isArray(tiles) ? tiles.length : 0

        setDisplayedProductCount(initialProductCount + loadedTilesCount)
    }, [productSearchResult?.hits, tiles])

    useEffect(() => {
        const selectedRefinements = productSearchResult?.selectedRefinements || {}

        const selectedRefinementsCount = Object.entries(selectedRefinements).reduce(
            (acc, [key, value]) => {
                if (key === 'htype' || key === 'cgid') return acc
                return acc + (value?.split('|')?.length || 0)
            },
            0
        )

        setSelectedFilterCount(selectedRefinementsCount)
    }, [productSearchResult?.selectedRefinements])

    useEffect(() => {
        setTiles([])
        setClickCount(0)
        setOffSetValue(0)
        setDisableShowMore(false)
    }, [params?.categoryId, searchQuery])

    useCarouselObserver(() => {
        carouselDataComponentCount()
    })
    useEffect(() => {
        if (!productSearchResult) return
        const total = productSearchResult.total
        if (
            (displayedProductCount === prevTotal && total > prevTotal) ||
            total < displayedProductCount
        ) {
            setTiles([])
            setClickCount(0)
            setOffSetValue(0)
            setDisableShowMore(false)
        }
        setPrevTotal(total)
    }, [productSearchResult?.selectedRefinements])

    const scrollbarStyles = {
        '::-webkit-scrollbar': {
            width: '6px',
            height: '6px'
        },
        '::-webkit-scrollbar-thumb': {
            background: '#dddddd',
            borderRadius: '6px',
            height: '6px'
        },
        '::-webkit-scrollbar-thumb:hover': {
            background: '#dddddd',
            height: '6px'
        }
    }

    const containerStyles = {
        padding: {
            base: '0 10px',
            sm: '0 20px',
            lg: '0 64px'
        },
        maxWidth: {
            base: '100%',
            lg: '1920px'
        },
        margin: '0 auto'
    }

    const defaultImageUrl = category?.c_slotBannerImage
    const mobileImageUrl = category?.c_slotBannerMobileImage || defaultImageUrl

    return (
        <Box
            sx={{...customStylesPLP, ...einsteinProductRecommendationStyles}}
            className="sf-product-list-page"
            data-testid="sf-product-list-page"
            bgColor="secondaryPageBgColor"
            {...rest}
        >
            <Helmet>
                <meta name="robots" content="noindex" />
                {productSearchResult?.pageMetaTags?.map(({id, value}) => {
                    if (id === 'title') {
                        return <title key={id}>{value}</title>
                    }
                    return <meta name={id} content={value} key={id} />
                })}
            </Helmet>

            {seoData && seoReady && (
                <Seo
                    key={location?.pathname}
                    hrefLangs={seoData.hrefLang}
                    title={metaTags?.title}
                    description={metaTags?.description}
                    keywords={metaTags?.keywords}
                ></Seo>
            )}
            {showNoResults ? (
                <EmptySearchResults searchQuery={searchQuery} category={category} />
            ) : (
                <>
                    <AbovePageHeader />
                    {isMarketingPersonalizationEnabled && (
                        <Box className="marketing-main-banner">
                            <GlobalContentSlotHtml
                                slotId={'personalization-article-recommendation'}
                                context={'global'}
                                type={'html'}
                            />
                        </Box>
                    )}

                    {(category?.c_slotBannerImage || category?.c_slotBannerMobileImage) && (
                        <Box
                            className="slant-down search-banner"
                            bg={category?.c_bannerBackgroundColor || ''}
                            pos={'relative'}
                            overflow="hidden"
                        >
                            {!isLoaded && (
                                <Skeleton
                                    height={{base: '110px', sm: '200px', lg: '450px'}}
                                    width="100%"
                                    position="absolute"
                                    top="0"
                                    left="0"
                                    right="0"
                                    bottom="0"
                                />
                            )}
                            <picture>
                                <source srcSet={`${mobileImageUrl}`} media="(max-width: 47.99em)" />
                                <Image
                                    src={defaultImageUrl}
                                    alt="Category Banner"
                                    margin="0 auto"
                                    opacity={isLoaded ? 1 : 0}
                                    transition="opacity 0.5s ease"
                                    onLoad={() => setIsLoaded(true)}
                                    onError={() => setIsLoaded(true)}
                                    fetchpriority="high"
                                />
                            </picture>
                        </Box>
                    )}

                    <Box data-testid={'plp-container'} sx={containerStyles}>
                        {/* Page Title */}
                        <Box
                            mb={{base: 4, sm: 6, md: 8}}
                            textAlign="center"
                            className="category-title-plp"
                        >
                            {seoCategoryTitleContent}
                        </Box>

                        <SubCategoryCarousel />

                        {/* Divider */}
                        <Box borderTop="1px solid #DADADA" />

                        {/* Breadcrumb */}
                        <Flex
                            data-testid="sf-product-list-breadcrumb"
                            justifyContent={{base: 'center', sm: 'left'}}
                            pt={searchQuery ? {base: 0, md: 2} : {base: 4, md: 8}}
                            pb={{base: 4, sm: 8, md: 6}}
                        >
                            {category && <Breadcrumb categories={category.parentCategoryTree} />}
                        </Flex>

                        {/* Result header*/}
                        <Flex
                            className={'result-header'}
                            justifyContent={{base: 'center', sm: 'space-between'}}
                            alignItems={'center'}
                        >
                            {/* top section */}
                            <Flex
                                direction={'row'}
                                alignItems={'baseline'}
                                gap={'1rem'}
                                data-testid="sf-search-result header"
                            >
                                <Flex className={'result-count'} fontWeight={'medium'}>
                                    {Object.keys(productSearchResult?.selectedRefinements || {})
                                        .length > 1 ||
                                    (Object.keys(productSearchResult?.selectedRefinements || {})
                                        .length === 1 &&
                                        !productSearchResult?.selectedRefinements?.cgid)
                                        ? formatMessage(
                                              {
                                                  id: 'product_list.filtered_count',
                                                  defaultMessage: '{totalHits} matching products'
                                              },
                                              {
                                                  totalHits: productSearchResult?.total
                                              }
                                          )
                                        : formatMessage(
                                              {
                                                  id: 'product_list.total_count',
                                                  defaultMessage: '{totalProductCount} Products'
                                              },
                                              {
                                                  totalProductCount: productSearchResult?.total
                                              }
                                          )}
                                </Flex>
                            </Flex>

                            <Flex display={{base: 'none', sm: 'flex'}} justifyContent="end">
                                <Box data-testid={'filter-button'} height={'48px'}>
                                    <Button
                                        leftIcon={
                                            hideFilter ? (
                                                selectedFilterCount > 0 ? (
                                                    <FilterOrange boxSize={6} />
                                                ) : (
                                                    <FilterIconCR boxSize={6} />
                                                )
                                            ) : (
                                                <FilterIconCR boxSize={6} color="black" />
                                            )
                                        }
                                        onClick={showhidefilter}
                                        {...filterStyles.filterButton}
                                        color={
                                            hideFilter && selectedFilterCount > 0
                                                ? '#ff640a'
                                                : 'black'
                                        }
                                        borderColor={
                                            hideFilter && selectedFilterCount > 0
                                                ? '#ff640a'
                                                : '#707070'
                                        }
                                    >
                                        {hideFilter ? (
                                            <>
                                                <FormattedMessage
                                                    defaultMessage="Filters"
                                                    id="product_list.button.show.filters"
                                                />
                                                {selectedFilterCount > 0 &&
                                                    ` (${selectedFilterCount})`}
                                            </>
                                        ) : (
                                            <FormattedMessage
                                                defaultMessage="Hide Filters"
                                                id="product_list.button.hide.filters"
                                            />
                                        )}
                                        <span
                                            style={{position: 'absolute', left: '-9999px'}}
                                            aria-live="polite"
                                        >
                                            {hideFilter ? 'Show Filters' : 'Hide Filters'}
                                        </span>
                                    </Button>
                                </Box>

                                <Sort
                                    sortUrls={sortUrls}
                                    productSearchResult={productSearchResult}
                                    // basePath={basePath}
                                />
                            </Flex>
                        </Flex>

                        {/* Show this section only if there are selected refinements for availability */}
                        {hideFilter && isTablet && hasSelectedFilters && (
                            <Stack className={'selectedFilters'} marginBottom={3}>
                                <SelectedRefinements
                                    filters={productSearchResult?.refinements}
                                    toggleFilter={toggleFilter}
                                    handleReset={resetFilters}
                                    selectedFilterValues={productSearchResult?.selectedRefinements}
                                    onSelectedFilterCountChange={setSelectedFilterCount}
                                />
                            </Stack>
                        )}

                        {/* Product tiles wrapper */}
                        <Box
                            className={'filter-productlist-wrapper'}
                            mt={{base: '4', sm: '8', xl: '6'}}
                        >
                            <Box
                                display={{base: 'flex', sm: 'none'}}
                                className={'mobileChanges'}
                                justifyContent={'space-between'}
                                marginBottom={8}
                            >
                                <Button
                                    {...filterStyles.filterButton}
                                    leftIcon={
                                        selectedFilterCount > 0 ? (
                                            <FilterOrange boxSize={6} />
                                        ) : (
                                            <FilterIconCR boxSize={5} />
                                        )
                                    }
                                    color={selectedFilterCount > 0 ? '#ff640a' : 'black'}
                                    borderColor={selectedFilterCount > 0 ? '#ff640a' : '#707070'}
                                    onClick={onOpen}
                                    width={'50%'}
                                    height={'48px'}
                                    borderRadius={'8px'}
                                    fontSize={'md'}
                                >
                                    <>
                                        <FormattedMessage
                                            defaultMessage="Filters"
                                            id="product_list.button.show.filters"
                                        />
                                        {selectedFilterCount > 0 && ` (${selectedFilterCount})`}
                                    </>
                                </Button>
                                <Box
                                    position="relative"
                                    display="flex"
                                    alignItems={'center'}
                                    width="50%"
                                    border={'1px solid #59595b;'}
                                    fontWeight={'bold'}
                                    height={'48px'}
                                    borderRadius={'8px'}
                                    className="sort-box"
                                >
                                    <Select
                                        aria-label="Sort options"
                                        value={
                                            sortUrls.find((href, index) => {
                                                const sortId =
                                                    productSearchResult?.sortingOptions?.[index]?.id
                                                const currentSort = urlParams.get('sort')
                                                return sortId === currentSort
                                            }) || sortUrls[0]
                                        }
                                        onChange={({target}) => {
                                            history?.push(target.value)
                                        }}
                                        backgroundImage={`url(${getAssetUrl(
                                            'static/img/select-dropdown.png'
                                        )})`}
                                    >
                                        {sortUrls?.map((href, index) => (
                                            <option key={href} value={href}>
                                                {intl.formatMessage(
                                                    {
                                                        id: 'product_list.select.sort_by_option',
                                                        defaultMessage: '{sortOption}'
                                                    },
                                                    {
                                                        sortOption:
                                                            productSearchResult?.sortingOptions[
                                                                index
                                                            ]?.label
                                                    }
                                                )}
                                            </option>
                                        ))}
                                    </Select>
                                    <SortIcon
                                        height="20px"
                                        width="20px"
                                        position="absolute"
                                        left="10px"
                                        top="14px"
                                    />
                                </Box>
                            </Box>

                            {/* Show this section only if there are selected refinements for availability */}
                            {isMobile && hasSelectedFilters && (
                                <Stack className={'selectedFilters'} marginBottom={3}>
                                    <SelectedRefinements
                                        filters={productSearchResult?.refinements}
                                        toggleFilter={toggleFilter}
                                        handleReset={resetFilters}
                                        selectedFilterValues={
                                            productSearchResult?.selectedRefinements
                                        }
                                        onSelectedFilterCountChange={setSelectedFilterCount}
                                    />
                                </Stack>
                            )}

                            <Flex justifyContent={'space-between'}>
                                <Box
                                    sx={{...scrollbarStyles}}
                                    w={hideFilter ? '0' : filterWidth}
                                    // maxW={'304px'}
                                    opacity={hideFilter ? '0' : '1'}
                                    transition={
                                        isTransitionEnabled || transitOnFilter
                                            ? 'all 0.3s ease 0s'
                                            : ''
                                    }
                                    overflowY="scroll"
                                    scrollBehavior="inside"
                                    position="sticky"
                                    top={'130px'}
                                    h="80vh"
                                    mr={hideFilter ? '0' : {base: '0', sm: '24px', lg: '32px'}}
                                    p={hideFilter ? '0' : {base: '0', sm: '2px'}}
                                    visibility={hideFilter ? 'hidden' : 'visible'}
                                >
                                    {/* Show this section only if there are selected refinements for availability */}
                                    {hasSelectedFilters && (
                                        <Stack className={'selectedFilters'} marginBottom={3}>
                                            <SelectedRefinements
                                                filters={productSearchResult?.refinements}
                                                toggleFilter={toggleFilter}
                                                handleReset={resetFilters}
                                                selectedFilterValues={
                                                    productSearchResult?.selectedRefinements
                                                }
                                                onSelectedFilterCountChange={setSelectedFilterCount}
                                            />
                                        </Stack>
                                    )}

                                    <CategoryFilter
                                        category={category}
                                        showMore={showMore}
                                        toggleShowMore={toggleShowMore}
                                        filterStyles={filterStyles}
                                        categoryUrlBuilder={categoryUrlBuilder}
                                    />

                                    <Box {...filterStyles.filterHeading}>
                                        <FormattedMessage
                                            defaultMessage="Filter By:"
                                            id="product_list.modal.title.filter_by"
                                        />
                                    </Box>

                                    <Stack display={{base: 'none', sm: 'flex', lg: 'flex'}}>
                                        <Refinements
                                            isLoading={filtersLoading}
                                            toggleFilter={(value, attributeId, isChecked) => {
                                                toggleFilter(value, attributeId, isChecked)
                                                setHasCheckedFilters(!isChecked)
                                            }}
                                            filters={productSearchResult?.refinements}
                                            selectedFilters={searchParams.refine}
                                            productSearchResultPrms={{
                                                query: productSearchResult?.query,
                                                cgid: productSearchResult?.selectedRefinements?.cgid
                                            }}
                                        />
                                    </Stack>
                                </Box>

                                <Box
                                    width={{
                                        base: '100%',
                                        md: hideFilter ? '100%' : '80%',
                                        lg: hideFilter ? '100%' : '75%'
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        flexWrap="wrap"
                                        gap={{base: '4', md: '8'}}
                                        className={
                                            hideFilter
                                                ? 'productlists-main-block filter-hidden'
                                                : 'productlists-main-block filter-active'
                                        }
                                        mb={{base: '4', md: '8'}}
                                    >
                                        {isHydrated() &&
                                        ((isRefetching && !isFetched) || !productSearchResult)
                                            ? new Array(searchParams.limit)
                                                  .fill(0)
                                                  .map((value, index) => (
                                                      <ProductTileSkeleton key={index} />
                                                  ))
                                            : productSearchResult?.hits?.map(
                                                  (productSearchItem, index) => {
                                                      const productId = productSearchItem.productId
                                                      const isInWishlist =
                                                          !!wishlist?.productWishList?.some(
                                                              (list) =>
                                                                  list?.customerProductListItems?.some(
                                                                      (item) =>
                                                                          item?.productId ===
                                                                          productId
                                                                  )
                                                          )

                                                      return (
                                                          <ProductTile
                                                              zIndex={0}
                                                              productPosition={index}
                                                              data-testid={`sf-product-tile-${productSearchItem.productId}`}
                                                              key={productSearchItem.productId}
                                                              product={productSearchItem}
                                                              enableFavourite={true}
                                                              showHorizontalTileInMobile={true}
                                                              isFavourite={isInWishlist}
                                                              aria-label={
                                                                  productSearchItem.productName
                                                              }
                                                              onClick={() => {
                                                                  // einstein
                                                                  if (searchQuery) {
                                                                      einstein.sendClickSearch(
                                                                          searchQuery,
                                                                          productSearchItem
                                                                      )
                                                                  } else if (category) {
                                                                      einstein.sendClickCategory(
                                                                          category,
                                                                          productSearchItem
                                                                      )
                                                                  }
                                                              }}
                                                              onFavouriteToggle={(
                                                                  isFavourite,
                                                                  productDetail,
                                                                  event
                                                              ) => {
                                                                  const action = isFavourite
                                                                      ? (product, e) =>
                                                                            addItemToWishlist(
                                                                                e,
                                                                                product,
                                                                                index,
                                                                                productDetail
                                                                            )
                                                                      : (product, e) =>
                                                                            removeItemFromWishlist(
                                                                                e,
                                                                                product,
                                                                                index,
                                                                                productDetail
                                                                            )
                                                                  return action(
                                                                      productSearchItem,
                                                                      event
                                                                  )
                                                              }}
                                                              dynamicImageProps={{
                                                                  widths: [
                                                                      '50vw',
                                                                      '50vw',
                                                                      '20vw',
                                                                      '20vw',
                                                                      '25vw'
                                                                  ],
                                                                  disWidth: 400
                                                              }}
                                                          />
                                                      )
                                                  }
                                              )}
                                        {clickCount > 0 && (
                                            <AppendTiles
                                                key={clickCount}
                                                offSetValue={offSetValue}
                                                setOffSetValue={setOffSetValue}
                                                searchParams={searchParams}
                                                setTiles={setTiles}
                                                setDisableShowMore={setDisableShowMore}
                                                triggerSearch={triggerSearch}
                                                onComplete={() => setTriggerSearch(false)}
                                            />
                                        )}

                                        {/* slice is used to skip the initial loaded tiles */}
                                        {tiles?.map((tile, index) => {
                                            const productId = tile.productId
                                            const isInWishlist = !!wishlist?.productWishList?.some(
                                                (list) =>
                                                    list?.customerProductListItems?.some(
                                                        (item) => item?.productId === productId
                                                    )
                                            )
                                            return (
                                                <ProductTile
                                                    zIndex={0}
                                                    productPosition={index}
                                                    data-testid={`sf-product-tile-${tile.productId}`}
                                                    key={index + '-' + tile.productId}
                                                    product={tile}
                                                    enableFavourite={true}
                                                    showHorizontalTileInMobile={true}
                                                    isFavourite={isInWishlist}
                                                    aria-label={tile.productName}
                                                    onClick={() => {
                                                        if (isTAEnabled) {
                                                            trackTAEvent({
                                                                c_productId: tile.productId,
                                                                c_refUseCase: 'CRSTORE',
                                                                c_useCase:
                                                                    'Store%2FLearn%2FClickedOnSearch',
                                                                additionalData: {
                                                                    isSearch: true
                                                                }
                                                            })
                                                        }
                                                        // segment
                                                        segment.sendProductClick(tile, index)
                                                    }}
                                                    onFavouriteToggle={(
                                                        isFavourite,
                                                        productDetail,
                                                        event
                                                    ) => {
                                                        const action = isFavourite
                                                            ? (product, e) =>
                                                                  addItemToWishlist(
                                                                      e,
                                                                      product,
                                                                      index,
                                                                      productDetail
                                                                  )
                                                            : (product, e) =>
                                                                  removeItemFromWishlist(
                                                                      e,
                                                                      product,
                                                                      index,
                                                                      productDetail
                                                                  )
                                                        return action(tile, event)
                                                    }}
                                                    dynamicImageProps={{
                                                        widths: [
                                                            '50vw',
                                                            '50vw',
                                                            '20vw',
                                                            '20vw',
                                                            '25vw'
                                                        ],
                                                        disWidth: 400
                                                    }}
                                                />
                                            )
                                        })}
                                    </Box>
                                    <CategoryContentAsset
                                        id={'category-seo-' + params?.categoryId}
                                    />
                                </Box>
                            </Flex>

                            <Box
                                data-testid="pagination"
                                mb={{base: '4', lg: '8'}}
                                textAlign={'center'}
                            >
                                <Box className={'result-count-bottom'} mb={4}>
                                    {showLoadMore &&
                                    displayedProductCount < productSearchResult?.total ? (
                                        formatMessage(
                                            {
                                                id: 'product_list.count',
                                                defaultMessage:
                                                    'Showing {displayedCount} of {totalProductCount} results'
                                            },
                                            {
                                                totalProductCount: productSearchResult?.total,
                                                displayedCount: displayedProductCount
                                            }
                                        )
                                    ) : (
                                        <></>
                                    )}
                                </Box>
                                {showLoadMore &&
                                displayedProductCount < productSearchResult?.total ? (
                                    <Button
                                        variant="outline"
                                        onClick={loadMore}
                                        isDisabled={disableShowMore}
                                        padding="0 52px"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Show More"
                                            id="product_list.button.show_more"
                                        />
                                    </Button>
                                ) : (
                                    <></>
                                )}
                            </Box>
                        </Box>

                        {/* Modal for filter options on mobile */}
                        <Modal
                            isOpen={isOpen}
                            onClose={onClose}
                            size="full"
                            motionPreset="slideInBottom"
                            scrollBehavior="inside"
                            data-testid={'mobile-filter-modal'}
                        >
                            <ModalOverlay />
                            <ModalContent
                                top={0}
                                marginTop={0}
                                bgColor="#f8f6f3"
                                sx={customStylesPLP}
                            >
                                <ModalBody pt={18}>
                                    {/* Show this section only if there are selected refinements for availability */}
                                    {hasSelectedFilters && (
                                        <SelectedRefinements
                                            filters={productSearchResult?.refinements}
                                            toggleFilter={toggleFilter}
                                            handleReset={resetFilters}
                                            selectedFilterValues={
                                                productSearchResult?.selectedRefinements
                                            }
                                            onSelectedFilterCountChange={setSelectedFilterCount}
                                            mb={4}
                                        />
                                    )}

                                    <Box position={'relative'}>
                                        <ModalCloseButton
                                            fontSize={'lg'}
                                            size={10}
                                            top={'10px'}
                                            right={'0'}
                                        />
                                        <CategoryFilter
                                            category={category}
                                            showMore={showMore}
                                            toggleShowMore={toggleShowMore}
                                            filterStyles={filterStyles}
                                            categoryUrlBuilder={categoryUrlBuilder}
                                        />
                                    </Box>

                                    <Box {...filterStyles.filterHeading} pt={0}>
                                        <FormattedMessage
                                            defaultMessage="Filter By:"
                                            id="product_list.modal.title.filter_by"
                                        />
                                    </Box>

                                    <Refinements
                                        isLoading={filtersLoading}
                                        toggleFilter={(value, attributeId, isChecked) => {
                                            // Just update filters, do not close modal
                                            toggleFilter(value, attributeId, isChecked)
                                            setHasCheckedFilters(!isChecked)
                                        }}
                                        filters={productSearchResult?.refinements}
                                        selectedFilters={searchParams.refine}
                                        isInMobileModal={true} // Flag to indicate this is in a mobile modal
                                        productSearchResultPrms={{
                                            query: productSearchResult?.query,
                                            cgid: productSearchResult?.selectedRefinements?.cgid
                                        }}
                                    />
                                </ModalBody>

                                <ModalFooter
                                    // justify="space-between"
                                    display="block"
                                    width="full"
                                    borderTop="1px solid"
                                    borderColor="#DADADA"
                                >
                                    <Stack direction={'column'}>
                                        <Button variant="secondaryFull" onClick={handleViewItems}>
                                            <FormattedMessage
                                                defaultMessage="View"
                                                id="filters_button.view"
                                            />{' '}
                                            {productSearchResult?.total} Items
                                        </Button>
                                        {hasCheckedFilters ? (
                                            <Button variant="secondaryFull" onClick={resetFilters}>
                                                <FormattedMessage
                                                    defaultMessage="Clear"
                                                    id="filters_button.clear"
                                                />
                                            </Button>
                                        ) : (
                                            <Button variant="outlineFull" onClick={onClose}>
                                                <FormattedMessage
                                                    defaultMessage="Clear"
                                                    id="filters_button.clear"
                                                />
                                            </Button>
                                        )}
                                    </Stack>
                                </ModalFooter>
                            </ModalContent>
                        </Modal>
                        <Drawer
                            placement="bottom"
                            isOpen={sortOpen}
                            onClose={() => setSortOpen(false)}
                            size="sm"
                            motionPreset="slideInBottom"
                            scrollBehavior="inside"
                            isFullHeight={false}
                            height="50%"
                        >
                            <DrawerOverlay />
                            <DrawerContent marginTop={0}>
                                <DrawerCloseButton />
                                <DrawerBody>
                                    {sortUrls.map((href, idx) => (
                                        <Button
                                            width="full"
                                            onClick={() => {
                                                setSortOpen(false)
                                                history.push(href)
                                            }}
                                            fontSize={'md'}
                                            key={idx}
                                            marginTop={0}
                                            variant="menu-link"
                                        >
                                            <Text
                                                as={
                                                    selectedSortingOptionLabel?.label ===
                                                        productSearchResult?.sortingOptions[idx]
                                                            ?.label && 'u'
                                                }
                                            >
                                                {productSearchResult?.sortingOptions[idx]?.label}
                                            </Text>
                                        </Button>
                                    ))}
                                </DrawerBody>
                            </DrawerContent>
                        </Drawer>

                        {/* Divider */}
                        <Box borderTop="1px solid #DADADA" mb={8} />
                    </Box>
                    {/* container closing Box*/}
                    {/* Carousels */}
                    <Box
                        backgroundImage={`url(${getAssetUrl(
                            'static/img/bg_shop_bottom_halftone.png'
                        )})`}
                        backgroundPosition="0 100%"
                        backgroundSize="auto"
                        backgroundRepeat="repeat-x"
                        sx={productCarouselStyles}
                    >
                        <Box className={'einsteinRecommendations'} sx={containerStyles}>
                            <EinsteinTaSlotHtml
                                slotId={'plp-product-recommendations-m'}
                                context={'global'}
                                type={'html'}
                            />
                            <EinsteinTaSlotHtml
                                slotId={'plp-product-recommendations2-m'}
                                context={'global'}
                                type={'html'}
                            />
                            <EinsteinTaSlotHtml
                                slotId={'plp-product-recommendations3-m'}
                                context={'global'}
                                type={'html'}
                            />

                            {isMarketingPersonalizationEnabled && (
                                <Box className="marketing-product-recommendation">
                                    <GlobalContentSlotHtml
                                        slotId={'personalization-article-recommendation'}
                                        context={'global'}
                                        type={'html'}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    )
}

export default CategoryContent

const Sort = ({sortUrls, productSearchResult, ...otherProps}) => {
    const intl = useIntl()
    const history = useHistory()
    const location = useLocation()
    const urlParams = new URLSearchParams(location.search)
    const currentSort = urlParams.get('sort')
    const announcerRef = useRef(null)

    const announce = (text) => {
        const el = announcerRef.current
        if (!el) return
        el.textContent = ''
        setTimeout(() => {
            el.textContent = text
        }, 120)
    }

    const handleMouseEnter = (e) => {
        const selectedText = e.currentTarget.options[e.currentTarget.selectedIndex]?.text || ''
        announce(`Sort options. Currently selected: ${selectedText}`)
    }

    return (
        <FormControl
            aria-label={intl.formatMessage({
                id: 'product_list.drawer.title.sort_by',
                defaultMessage: 'Sort By'
            })}
            data-testid="sf-product-list-sort"
            id="page_sort"
            {...otherProps}
        >
            <Box
                position="relative"
                display="flex"
                alignItems={'center'}
                width="fit-content"
                border={'1px solid #59595b;'}
                fontWeight={'bold'}
                height={'48px'}
                borderRadius={'8px'}
                className="sort-box"
            >
                <Select
                    aria-label="Sort options"
                    value={
                        sortUrls.find((href, index) => {
                            const sortId = productSearchResult?.sortingOptions?.[index]?.id
                            return sortId === currentSort
                        }) || sortUrls[0]
                    }
                    onChange={({target}) => {
                        history?.push(target.value)
                    }}
                    onMouseEnter={handleMouseEnter}
                    border={'none'}
                    fontWeight={'extrabold'}
                    lineHeight={5}
                    cursor={'pointer'}
                    backgroundImage={`url(${getAssetUrl('static/img/select-dropdown.png')})`}
                    backgroundRepeat={'no-repeat'}
                    height={'auto'}
                    backgroundPosition={'center right 16px'}
                >
                    {sortUrls?.map((href, index) => (
                        <option key={href} value={href}>
                            {intl.formatMessage(
                                {
                                    id: 'product_list.select.sort_by_option',
                                    defaultMessage: '{sortOption}'
                                },
                                {
                                    sortOption: productSearchResult?.sortingOptions[index]?.label
                                }
                            )}
                        </option>
                    ))}
                </Select>
                <VisuallyHidden>
                    {/* role/status + aria-atomic helps SR read whole message */}
                    <div ref={announcerRef} role="status" aria-live="polite" aria-atomic="true" />
                </VisuallyHidden>
                <SortIcon boxSize={6} position="absolute" left="10px" top="10px" />
            </Box>
        </FormControl>
    )
}

Sort.propTypes = {
    sortUrls: PropTypes.array,
    productSearchResult: PropTypes.object
}

const CategoryFilter = ({category, showMore, toggleShowMore, filterStyles, categoryUrlBuilder}) => {
    if (!category?.categories) return null

    const hasExtraItems = category.categories.length > 6

    return (
        <>
            <Box
                data-testid="category-filter"
                borderBottom={'1px solid #DADADA'}
                mb={{base: 4, sm: 0}}
            >
                <Box {...filterStyles.filterHeading} pt={0}>
                    {category.name}
                </Box>

                <Box data-testid="category-filter-item" marginTop={4} pb={4} lineHeight="2rem">
                    <Stack as="ul" display="block" mt={4}>
                        {category.categories.slice(0, 6).map((item, index) => (
                            <Text as="li" key={index} {...filterStyles.filterItem}>
                                <Link to={categoryUrlBuilder(item)}>{item?.name}</Link>
                            </Text>
                        ))}
                    </Stack>

                    {showMore && hasExtraItems && (
                        <Stack as="ul" display="block" transition="0.3s ease-in-out">
                            {category.categories.slice(6).map((item, index) => (
                                <Text as="li" key={index} {...filterStyles.filterItem}>
                                    <Link to={categoryUrlBuilder(item)}>{item?.name}</Link>
                                </Text>
                            ))}
                        </Stack>
                    )}

                    {category.categories.length !== 1 && hasExtraItems && (
                        <Link
                            as={'button'}
                            onClick={toggleShowMore}
                            data-testid="filter-showmore-refinement-item"
                            mb={3}
                            {...filterStyles.showMore}
                            aria-label={showMore ? 'Show Less' : 'Show More'}
                        >
                            {showMore ? (
                                <span aria-hidden="true">
                                    <FormattedMessage
                                        defaultMessage="Show Less"
                                        id="product_list.button.show.less"
                                    />
                                </span>
                            ) : (
                                <span aria-hidden="true">
                                    <FormattedMessage
                                        defaultMessage="Show More"
                                        id="product_list.button.hide.more"
                                    />
                                </span>
                            )}
                        </Link>
                    )}
                </Box>
            </Box>
        </>
    )
}

CategoryFilter.propTypes = {
    category: PropTypes.shape({
        name: PropTypes.string,
        categories: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                id: PropTypes.string // extend with more fields if needed
            })
        )
    }),
    showMore: PropTypes.bool.isRequired,
    toggleShowMore: PropTypes.func.isRequired,
    filterStyles: PropTypes.shape({
        filterHeading: PropTypes.object,
        filterItem: PropTypes.object,
        showMore: PropTypes.object
    }),
    categoryUrlBuilder: PropTypes.func.isRequired
}
